<template lang="pug">
.form-group(:class="{ 'has-error': hasError }")
  label.control-label
    | Track time to project
    abbr(
      v-if="required"
      title="required"
    ) *
  AppSelect.select-rule-start-project(
    :value="value"
    :disabled="disabled"
    :options="projectOptions"
    :select2-options="select2Options"
    :disabled-tooltip="disabledTooltip"
    @input="$emit('input', $event)"
  )
  //- Allow passing custom error from the parent
  slot(name="error")
</template>

<script>
import { mapGetters } from 'vuex';
import { CONSTANTS } from '~/app_constants';
import { difference, isEmpty, sortBy } from 'lodash-es';
import { getIdsForOptionType, partitionTeamsMembersIds } from '~/utils/job_sites';

/**
 * It has been agreed with Product, that the expand/collapse functionality
 * for the "Other projects" group inside the select is not a
 * requirement anymore and we can remove it.
 */
export default {
  name: 'NewJobSitesProjectSelect',
  props: {
    value: {
      type: Number,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    selectedTeamsMembers: {
      type: Array,
      default: () => [],
    },
    teamsEnabled: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return Object.freeze({
      disabledTooltip: `On arrival action needs to be set to "Start timer" in order to be able to track on a project`,
    });
  },
  computed: {
    ...mapGetters(['activeProjects']),
    select2Options() {
      return {
        placeholder: 'Select a project',
        width: '100%',
        language: {
          noResults: () => {
            return !this.hasSelectedUsers
              ? // #teams-addon
                this.teamsEnabled
                ? CONSTANTS.noMemberSelectedProjectWithTeamsTooltip
                : CONSTANTS.noMemberSelectedProjectTooltip
              : CONSTANTS.noProjectsTooltip;
          },
        },
      };
    },
    hasSelectedUsers() {
      return (this.selectedTeamsMembers || []).length > 0;
    },
    projectOptions() {
      let options = [];
      const other = {
        children: [],
        text: 'All other projects',
        // #teams-addon
        hint: this.teamsEnabled
          ? CONSTANTS.jobSiteRuleOtherProjectsWithTeamsTooltip
          : CONSTANTS.jobSiteRuleOtherProjectsTooltip,
      };

      if (!this.hasSelectedUsers || !this.activeProjects.length) {
        return options;
      }

      for (const project of this.activeProjects) {
        const { id, name, team_ids: teamIds, user_role_ids: userRoleIds } = project;
        const userIds = Object.keys(userRoleIds || {}).map(
          (userId) => getIdsForOptionType(parseInt(userId, 10), 'member').id,
        );
        const [selectedTeamIds, selectedUserIds] = partitionTeamsMembersIds(this.selectedTeamsMembers);
        const allSelectedTeamsHaveAccess = isEmpty(difference(selectedTeamIds, teamIds));
        const allSelectedUsersHaveAccess = isEmpty(difference(selectedUserIds, userIds));

        // Add project to the `otherProjects` options list if not all
        // selected teams or members belong to the project.
        if (allSelectedTeamsHaveAccess && allSelectedUsersHaveAccess) {
          options.push({ id, text: name });
        } else {
          other.children.push({ id, text: name });
        }
      }

      // Sort all projects by their name.
      options = sortBy(options, (opt) => opt.text.toLowerCase().trim());

      if (other.children.length) {
        other.children = sortBy(other.children, (opt) => opt.text.toLowerCase().trim());

        // Push the extra projects to which the selected members do not belong.
        options.push(other);
      }

      return options;
    },
  },
};
</script>
